/// <amd-module name="VSSUI/Utilities/DragDrop" />
import * as React from "react";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { Portal } from "VSSUI/Portal";
import { css, getPointByEventType, Pointer } from "../Util";
import { distance, IPoint } from "./Position";

/**
 * Represents the .detail property of our custom drag events.
 */
export interface IBoltDragEventDetail<D, S> {
    /**
     * The drag event's data - modeled after the HTML5 DnD
     * DataTransfer object: https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer
     * We cannot use this class directly as we need the ability to more freely
     * manipulate its properties.
     */
    readonly dataTransfer: IDragDataTransfer<D, S>;

    /**
     * The native keyboard or pointer event which we are translating into
     * a BoltDragEvent.
     */
    readonly nativeEvent: PointerEvent | KeyboardEvent;
}

/**
 * Encapsulates the data and status of a drag and drop operation. Modeled after the HTML5 DnD
 * DataTransfer object: https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer.
 * We do not use the DataTransfer class directly as it has some logic that prevents us
 * from freely manipulating its properties. The majority of the properties are intended to be
 * readonly, with the exception of the dropEffect, which consumers can modify in their event handlers
 * to indicate whether or not a drop is allowed on a specific target.
 */
export interface IDragDataTransfer<D, S> {
    /**
     * The primary data being acted upon during a drag and drop operation.
     */
    readonly data: D;

    /**
     * The result of the drag and drop operation if it were to be ended right now. This property gives
     * consumers a chance to indicate whether or not the operation should be allowed based on the current
     * drag data in conjunction with the drop target.
     */
    dropEffect: DragDropEffect;

    /**
     * The type of results this drag and drop operation supports. This property should only be modified
     * during the dragstart event, and can be used to cancel a drag operation. This is useful in the scenario
     * where a List is the drag source, but a given row within the list is not able to be dragged.
     */
    effectAllowed?: DragDropEffect;

    /**
     * Any secondary data related to the operation, but not directly used by
     * the DragDropManager. e.g. for list drag and drop, this can be the index
     * of the item being dragged.
     */
    readonly secondaryData?: S;

    /**
     * Allows consumer to change the drag image based on the latest event.
     * @param image - What to render
     * @param xOffset - Horizontal offset from the mouse position
     * @param yffset - Vertical offset from the mouse position
     */
    setDragImage: (image: React.ReactNode, xOffset?: number, yOffset?: number) => void;

    /**
     * Identifies the type of data being transferred. Allows us to not call drop
     * targets which have restrictions on the types of data that can be dropped on them.
     */
    readonly type: string;
}

/**
 * Represents the end result of a drag / drop operation.
 */
export const enum DragDropEffect {
    /**
     * If the drop where to happen at this point, it would be a no-op.
     */
    none = "none",

    /**
     * The data should be moved from the drag source to the drop target.
     */
    move = "move",

    /**
     * The data should be copied from the drag source to the drop target.
     */
    copy = "copy"
}

/**
 * Exposes the x/y coordinates of the current drag/drop operation.
 * Used to determine where to draw the drag image.
 */
export interface IDragDropOperation {
    /**
     * X offset of current drag/drop operation
     */
    x: IReadonlyObservableValue<number | undefined>;

    /**
     * Y offset of current drag/drop operation
     */
    y: IReadonlyObservableValue<number | undefined>;
}

/**
 * Extends the React.DragEvent to include our custom event's detail.
 * @typeparam T: The type of the HTMLElement that fired the event.
 * @typeparam D: The type of data in the dataTransfer object.
 * @typeparam S: The type of secondary data in the dataTransfer object.
 */
export interface BoltDragEvent<T, D, S> extends Pick<React.DragEvent<T>, Exclude<keyof React.DragEvent<T>, "detail">> {
    readonly detail: IBoltDragEventDetail<D, S>;
}

class DragDropManager {
    private dataTransfer: IDragDataTransfer<{}, {}>;
    private dragInProgress: boolean;
    private dragSourceElement: HTMLElement;
    private initialCoordinates: IPoint;
    private minimumPixelsForDrag: number;
    private operation: IDragDropOperation | undefined;
    private potentialDragInProgress: boolean;

    public beginDragOperation(
        event: React.PointerEvent<HTMLElement>,
        dataTransfer: IDragDataTransfer<{}, {}>,
        minimumPixelsForDrag = 4
    ): IDragDropOperation | undefined {
        this.operation = undefined;

        // Something (typically a pointdown on a drag source) has indicated that there is the potential
        // for a drag operation. If there is a drag operation already in progress, do nothing.
        if (!this.dragInProgress) {
            // If there is no drag operation in progress, we should set up the event handlers to detect pointer
            // operations that could lead us to actually start the drag / drop operation.

            if (event.type === "pointerdown") {
                this.startDrag(event, minimumPixelsForDrag, dataTransfer);
                this.initialCoordinates = {
                    x: (event as React.PointerEvent<HTMLElement>).clientX,
                    y: (event as React.PointerEvent<HTMLElement>).clientY
                };

                Pointer.setCapture(this.onEventCaptured);
                document.body.addEventListener("pointerout", this.onPointerOut, true);
                document.body.addEventListener("pointerover", this.onPointerOver, true);
                document.body.addEventListener("pointerleave", this.onPointerLeave);

                this.operation = {
                    x: new ObservableValue<number | undefined>(undefined),
                    y: new ObservableValue<number | undefined>(undefined)
                };
            }
        }

        return this.operation;
    }

    public get isDragInProgress() {
        return this.dragInProgress;
    }

    private endDrag() {
        document.body.removeEventListener("pointerout", this.onPointerOut);
        document.body.removeEventListener("pointerover", this.onPointerOver);
        document.body.removeEventListener("pointerleave", this.onPointerLeave);
        this.dragInProgress = false;
    }

    private getTargetFromEvent(event: PointerEvent) {
        return event.target;
    }

    private startDrag<D, S>(event: React.PointerEvent<HTMLElement>, minimumPixelsForDrag: number, dataTransfer: IDragDataTransfer<D, S>) {
        this.potentialDragInProgress = true;
        this.dragSourceElement = event.target as HTMLElement;
        this.minimumPixelsForDrag = minimumPixelsForDrag;
        this.dataTransfer = dataTransfer;
    }

    private onEventCaptured = (event: PointerEvent) => {
        // Handle the pointerup and pointermove events
        const { type } = event;
        if (type === "pointermove") {
            // For pointermove events, if there is no drag in progress, we need to check to see if the pointer
            // has moved far enough to meet our threshold for triggering a drag/drop operation.
            if (!this.dragInProgress) {
                if (this.potentialDragInProgress) {
                    const coordinates = getPointByEventType(event)!;
                    if (distance(this.initialCoordinates, coordinates) > this.minimumPixelsForDrag) {
                        // The position of the pointer is far enough away from our threshold to trigger a drag event.
                        // Fire the dragstart event to give the drag source an opportunity to cancel the operation
                        dispatchCustomDragEvent("dragstart", this.dragSourceElement, event, this.dataTransfer);

                        if (this.dataTransfer.effectAllowed === DragDropEffect.none) {
                            this.potentialDragInProgress = false;
                            this.endDrag();
                        } else {
                            this.dragInProgress = true;
                        }

                        event.preventDefault();
                    }
                }
                // If there isn't the potential for a drag, that means a consumer has already
                // indicated that we should cancel this drag event, so there is no need to continue to
                // check anything about this event.
            } else {
                // If there is a drag in progress, treat this as a dragover event.
                const target = this.getTargetFromEvent(event);
                if (target) {
                    const coordinates = getPointByEventType(event)!;
                    (this.operation!.x as ObservableValue<number | undefined>).value = coordinates.x;
                    (this.operation!.y as ObservableValue<number | undefined>).value = coordinates.y;

                    dispatchCustomDragEvent("dragover", target, event, this.dataTransfer);

                    event.preventDefault();
                }
            }
        } else if (type === "pointerup") {
            if (this.dragInProgress) {
                const target = this.getTargetFromEvent(event);

                // Always fire the dragend event when we get a pointerup, if there was a drag in progress.
                dispatchCustomDragEvent("dragend", this.dragSourceElement, event, this.dataTransfer);

                if (target && this.dataTransfer.dropEffect !== DragDropEffect.none) {
                    // Only fire a drop event if the dropEffect allows it.
                    dispatchCustomDragEvent("drop", target, event, this.dataTransfer);
                }
            }

            this.endDrag();
        }
    };

    private onPointerLeave = (event: PointerEvent) => {
        // The pointer has left the bounds of the body element, so a drop is not
        // viable at this point.
        this.dataTransfer.dropEffect = DragDropEffect.none;
    };

    private onPointerOut = (event: PointerEvent) => {
        if (event.target) {
            // The pointer has left an element, so we need to set the dropEffect to none.
            // The dragover event will fire, giving a new drop target the chance to
            // reset the effect.
            this.dataTransfer.dropEffect = DragDropEffect.none;
            dispatchCustomDragEvent("dragexit", event.target, event, this.dataTransfer);
        }
    };

    private onPointerOver = (event: PointerEvent) => {
        if (event.target) {
            // The pointer has entered an element, so we need to set the dropEffect to none.
            // The dragover event will fire, giving a new drop target the chance to
            // reset the effect.
            this.dataTransfer.dropEffect = DragDropEffect.none;
            dispatchCustomDragEvent<{}, {}>("dragenter", event.target, event, this.dataTransfer);
        }
    };
}

const dragDropManager = new DragDropManager();

export function beginDragOperation<D, S>(
    event: React.PointerEvent<HTMLElement>,
    dataTransfer: IDragDataTransfer<D, S>,
    minimumPixelsForDrag?: number
): IDragDropOperation | undefined {
    return dragDropManager.beginDragOperation(event, dataTransfer, minimumPixelsForDrag);
}

export function dispatchCustomDragEvent<D, S>(
    eventType: string,
    target: EventTarget,
    event: PointerEvent | KeyboardEvent,
    dataTransfer: IDragDataTransfer<D, S>
): CustomEvent {
    const customEvent = new CustomEvent(eventType, {
        bubbles: true,
        detail: { dataTransfer: dataTransfer, nativeEvent: event } as IBoltDragEventDetail<{}, {}>
    });
    target.dispatchEvent(customEvent);
    return customEvent;
}

export function getDragInProgress() {
    return dragDropManager.isDragInProgress;
}

export interface IDragItemProps {
    /**
     * Optional class name to place on the content of the portal - the first element
     *  the consumer can control.
     */
    className?: string;

    /**
     * The drag/drop operation which has the current x/y values for the pointer position
     */
    operation: IDragDropOperation;

    /**
     * Number of pixels to offset the image from the pointer position horizontally.
     * @default 5
     */
    xOffset?: number;

    /**
     * Number of pixels to offset the image from the pointer position vertically.
     * @default 5
     */
    yOffset?: number;
}

export const DragImage = (props: IDragItemProps & Readonly<{ children?: React.ReactNode }>) => {
    const { className, operation, xOffset = 5, yOffset = 5 } = props;
    return (
        <Portal className="bolt-drag-image-portal">
            <Observer x={operation.x} y={operation.y}>
                {(observedProps: { x: number | undefined; y: number | undefined }) => {
                    return observedProps.x !== undefined && observedProps.y !== undefined ? (
                        <div
                            className={css(className, "bolt-drag-image depth-16 absolute flex-row flex-center scroll-hidden justify-center")}
                            style={{ left: observedProps.x + xOffset + "px", top: observedProps.y + yOffset + "px" }}
                        >
                            {props.children}
                        </div>
                    ) : null;
                }}
            </Observer>
        </Portal>
    );
};
